import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../styles/QrPage.css";
import { AiFillHome } from "react-icons/ai";
import { db } from "../firebase";
import { collection, addDoc, query, where, getDocs, serverTimestamp, onSnapshot, orderBy } from "firebase/firestore"; 
import { getAuth } from "firebase/auth";
import imageCompression from "browser-image-compression";
import Swal from "sweetalert2";
import axios from "axios";

function Securityqrpage() {
  const { state } = useLocation();
  const qrData = state?.qrData;
  const navigate = useNavigate();

  const [rating, setRating] = useState(0); 
  const [hoverRating, setHoverRating] = useState(0);
  const [issueHotel, setIssueHotel] = useState("");
  const [issueMaintenance, setIssueMaintenance] = useState("");
  const [hotelName, setHotelName] = useState(""); 
  const [checklist, setChecklist] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [userLocation, setUserLocation] = useState({ lat: null, lng: null, accuracy: null });
  const [locationError, setLocationError] = useState("");
  const [locationPermissionGranted, setLocationPermissionGranted] = useState(false);
  const [locationPromptShown, setLocationPromptShown] = useState(false);
  const [comments, setComments] = useState([]);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [watchId, setWatchId] = useState(null);

  // 🔹 Check location permission on mount
  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationError("Geolocation is not supported by this browser.");
      setLocationPermissionGranted(false);
      localStorage.removeItem('locationPermissionGranted');
      return;
    }

    // Check if permission was previously granted
    const savedPermission = localStorage.getItem('locationPermissionGranted');
    
    // Request location permission with fast network-based location
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy
        });
        setLocationPermissionGranted(true);
        localStorage.setItem('locationPermissionGranted', 'true');
        
        // Continue watching location in background (optional, less frequent)
        const wId = navigator.geolocation.watchPosition(
          (pos) => {
            setUserLocation({
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
              accuracy: pos.coords.accuracy
            });
          },
          (err) => console.error("Location watch error:", err),
          { 
            enableHighAccuracy: false, // Network-based for speed
            timeout: 15000, 
            maximumAge: 300000 // Accept 5 minute old location
          }
        );
        
        setWatchId(wId);
      },
      (error) => {
        console.log("Initial location error:", error);
        // Don't block on initial load - let user click button
        if (savedPermission === 'true') {
          setLocationError("Please enable location in browser settings");
        } else {
          setLocationError("Location permission required");
        }
        setLocationPermissionGranted(false);
      },
      { 
        enableHighAccuracy: false, // Network location (WiFi/Cell towers) - FAST!
        timeout: 10000, // 10 seconds timeout
        maximumAge: 300000 // Accept 5 minute old cached location
      }
    );

    // Cleanup function
    return () => {
      if (watchId !== null) {
        navigator.geolocation.clearWatch(watchId);
      }
    };
  }, []); // Empty dependency array - runs once on mount

  // 🔹 Fetch previous comments
  useEffect(() => {
    const q = query(
      collection(db, "observations"),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const list = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setComments(list);
    });

    return () => unsubscribe();
  }, []);

  // Auto-fill hotel name
  useEffect(() => {
    if (state?.hotelName) setHotelName(state.hotelName);
  }, [state]);

  // Fetch checklist
  useEffect(() => {
    if (!qrData) return;
    const fetchChecklist = async () => {
      try {
        const locationsRef = collection(db, "locations");
        const q = query(locationsRef, where("qrValue", "==", qrData));
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
          const locationDoc = querySnapshot.docs[0].data();
          setChecklist(locationDoc.checklist || []);
        } else setChecklist([]);
      } catch (err) {
        console.error(err);
        setChecklist([]);
      }
    };
    fetchChecklist();
  }, [qrData]);

  const auth = getAuth();
  const userEmail = auth.currentUser?.email || null;

  async function fetchPublicIP() {
    try {
      const res = await fetch("https://api.ipify.org?format=json");
      if (!res.ok) throw new Error("Failed to get IP");
      const data = await res.json();
      return data.ip;
    } catch {
      return null;
    }
  }

  // Function to request location permission
  const requestLocationPermission = () => {
    // Show loading state
    Swal.fire({
      title: 'Getting Location...',
      html: 'This should only take a few seconds',
      allowOutsideClick: false,
      timer: 8000, // Auto-close after 8 seconds
      timerProgressBar: true,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    navigator.geolocation.getCurrentPosition(
      (position) => {
        // Close loading
        Swal.close();
        
        // Set location data
        const locationData = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy
        };
        
        setUserLocation(locationData);
        setLocationPermissionGranted(true);
        setLocationPromptShown(true);
        localStorage.setItem('locationPermissionGranted', 'true');
        
        console.log("Location granted:", locationData); // Debug log
        
        // Start watching location in background (less frequent updates)
        const wId = navigator.geolocation.watchPosition(
          (pos) => {
            setUserLocation({
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
              accuracy: pos.coords.accuracy
            });
          },
          (err) => console.error("Location watch error:", err),
          { 
            enableHighAccuracy: false, // Fast network-based location
            timeout: 15000, 
            maximumAge: 300000 // 5 minutes
          }
        );
        
        setWatchId(wId);
      },
      (error) => {
        // Close loading
        Swal.close();
        
        console.log("Location error:", error);
        
        // Handle different error types
        if (error.code === 1) {
          // Permission denied
          setLocationPromptShown(true);
          setLocationPermissionGranted(false);
          localStorage.removeItem('locationPermissionGranted');
          Swal.fire({
            icon: "warning",
            title: "Location Denied",
            text: "Please enable location permission in your browser settings.",
            confirmButtonText: "OK"
          });
        } else if (error.code === 3) {
          // Timeout - proceed anyway with approximate location
          Swal.fire({
            icon: "info",
            title: "Using Approximate Location",
            text: "GPS is taking longer. Proceeding with network-based location.",
            timer: 2000,
            showConfirmButton: false
          }).then(() => {
            setLocationPermissionGranted(true);
            localStorage.setItem('locationPermissionGranted', 'true');
            // Use approximate/last known location
            setUserLocation({ lat: null, lng: null, accuracy: null });
          });
        } else {
          // Other errors - still allow to proceed
          Swal.fire({
            icon: "info",
            title: "Location Unavailable",
            text: "Proceeding without precise location.",
            timer: 2000,
            showConfirmButton: false
          }).then(() => {
            setLocationPermissionGranted(true);
            localStorage.setItem('locationPermissionGranted', 'true');
            setUserLocation({ lat: null, lng: null, accuracy: null });
          });
        }
        
        setLocationPromptShown(true);
      },
      { 
        enableHighAccuracy: false, // Network-based (WiFi/Cell) - VERY FAST!
        timeout: 8000, // 8 seconds - much faster
        maximumAge: 300000 // Accept 5 minute old location
      }
    );
  };

  async function handleSubmit(e) {
    e.preventDefault(); 
    setError(""); 
    
    if (!locationPermissionGranted || !userLocation.lat) {
      requestLocationPermission();
      return;
    }

    if (!userEmail) {
      setError("No logged-in user found. Please sign in first.");
      return;
    }

    setSubmitting(true); 
    try {
      const ip = await fetchPublicIP(); 

      // Upload images to Cloudinary
      const imageUrls = [];
      for (let img of uploadedImages) {
        const url = await uploadToCloudinary(img);
        if (url) imageUrls.push(url);
      }
    
      const payload = {
        qrData: qrData || null,
        userEmail,
        hotelName,
        issueHotel,
        issueMaintenance,
        rating,
        userIp: ip,
        location: userLocation,
        createdAt: serverTimestamp(),
        imageUrls,
      };

      await addDoc(collection(db, "reviews"), payload);
      
      // Show success and redirect
      await Swal.fire({
        icon: "success",
        title: "Submitted!",
        text: "Review submitted successfully",
        timer: 1500,
        showConfirmButton: false
      });
      
      // Redirect to dashboard
      navigate("/securitydash");
      
    } catch (err) {
      console.error(err);
      setError("Failed to submit. Try again.");
      Swal.fire({
        icon: "error",
        title: "Submission Failed",
        text: "Could not submit review. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  }

  async function uploadToCloudinary(file) {
    if (!file) return null;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", "qrdetails"); 

    try {
      const res = await axios.post(
        "https://api.cloudinary.com/v1_1/dmrobupcg/image/upload", 
        formData,
        {
          params: {
            quality: "auto:low",
          }
        }
      );
      return res.data.secure_url;
    } catch (err) {
      console.error("Cloudinary upload failed:", err);
      return null;
    }
  }
   
  async function handleCameraCapture(e) {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      try {
        const options = {
          maxSizeMB: 0.5,
          maxWidthOrHeight: 1024,
          useWebWorker: true,
        };

        const compressedFile = await imageCompression(file, options);
        setUploadedImages((prev) => [...prev, compressedFile]);

        Swal.fire({
          icon: "success",
          title: "Photo Captured",
          text: `Photo compressed to ${(compressedFile.size / 1024 / 1024).toFixed(2)} MB`,
          timer: 1500,
          showConfirmButton: false
        });
      } catch (error) {
        console.error("Compression failed:", error);
        Swal.fire({
          icon: "error",
          title: "Image Error",
          text: "Could not process image. Please try again.",
        });
      }
    }
  }

  // Block form if location not granted
  if (!locationPermissionGranted) {
    return (
      <div className="qrpage-root">
        <button
          className="home-icon-button"
          onClick={() => navigate("/securitydash")}
          title="Back to Dashboard"
        >
          <AiFillHome className="home-icon" />
        </button>
        
        <div className="qrpage-container location-required">
          <div className="location-icon">📍</div>
          <h2>Location Access Required</h2>
          <p>This security check requires your location to verify your position. Your location data is only used for security purposes and will be stored with your report.</p>
          <div className="location-info-box">
            <p><strong>Why we need this:</strong></p>
            <ul>
              <li>Verify security personnel location</li>
              <li>Track patrol routes</li>
              <li>Ensure on-site reporting</li>
            </ul>
          </div>
          <button 
            onClick={requestLocationPermission} 
            className="btn-primary"
          >
            📍 Enable Location
          </button>
          <button 
            onClick={() => navigate("/securitydash")} 
            className="btn-secondary"
            style={{ marginTop: "10px" }}
          >
            Go Back to Dashboard
          </button>
          {locationPromptShown && (
            <p className="location-hint">
              If you denied location access, please enable it in your browser settings and try again.
            </p>
          )}
        </div>
      </div>
    );
  }

  console.log("Rendering form with location:", userLocation); // Debug log

  return (
    <div className="qrpage-root">
      <button
        className="home-icon-button"
        onClick={() => navigate("/securitydash")}
        title="Back to Dashboard"
      >
        <AiFillHome className="home-icon" />
      </button>

      <div className="qrpage-container">
        <div className="header-section">
          <h2 style={{ color: 'black' }}>Security Check Report</h2>
          <div className="location-badge">
            <span className="location-dot"></span>
            Location Active
          </div>
        </div> 

        <div className="info-card">
          <div className="info-row">
            <span className="info-label">Hotel:</span>
            <span className="info-value">{hotelName || "Not Found"}</span>
          </div>
          <div className="info-row">
            <span className="info-label">QR Code:</span>
            <span className="info-value qr-code-text">{qrData || "No QR code data found"}</span>
          </div>
        </div>

        <form className="qr-form" onSubmit={handleSubmit}> 
          <div className="form-group">
            <label>Comments</label>
            <textarea
              value={issueMaintenance}
              onChange={(e) => setIssueMaintenance(e.target.value)}
              placeholder="Enter your observations here..."
              rows={4}
            />
          </div>

          <div className="form-group">
            <label>Capture Photos</label>
            <div className="file-input-wrapper">
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleCameraCapture}
                id="camera-input"
              />
              <label htmlFor="camera-input" className="file-input-label">
                📷 Take Photo
              </label>
            </div>
          </div>

          {uploadedImages?.length > 0 && (
            <div className="image-preview-grid">
              {uploadedImages.map((img, idx) => (
                <div key={idx} className="preview-thumbnail-wrapper">
                  <img
                    src={URL.createObjectURL(img)}
                    alt={`Preview ${idx + 1}`}
                    className="preview-thumbnail"
                  />
                  <button
                    type="button"
                    className="remove-btn"
                    onClick={() =>
                      setUploadedImages((prev) => prev.filter((_, i) => i !== idx))
                    }
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}

          {error && <div className="form-error">{error}</div>}

          <div className="button-row">
            <button
              type="button"
              onClick={() => navigate("/securityscanqr", { state: { hotelName } })}
              className="btn-secondary"
            >
              Scan Again
            </button>

            <button
              type="submit"
              className="btn-primary"
              disabled={submitting}
            >
              {submitting ? "Submitting..." : "Submit Report"}
            </button>
          </div>

          <div className="notice-card">
            <div className="notice-header">
              <span className="notice-icon">ℹ️</span>
              <strong>Important Notice</strong>
            </div>
            <p>This form is for reporting security checklist items only. Please do not use it for general feedback or inquiries.</p>
            
            {checklist.length > 0 && (
              <div className="checklist-section">
                <h4>Checklist Items:</h4>
                <ul className="checklist-items">
                  {checklist.map((item, idx) => (
                    <li key={idx}>
                      <span className="check-icon">✓</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

export default Securityqrpage;